import { useCallback } from "react";
import type { EditorStore } from "../../../state/editor/EditorStore";
import type { BindingCtx } from "../../../state/editor/types";
import { useEditorStore } from "../../../state/editor/useEditor";
import { basicEngineFor } from "../basic/applyEngine";
import type { Binding } from "./schema";

/**
 * Advanced edits, undoable.
 *
 * Before this, Undo in the top bar only knew what Basic mode had done: drag an
 * Advanced slider, press Undo, and it silently took back the last Basic step
 * instead — the one edit you meant stayed and one you did not was reverted.
 * Every bound row now writes through `useRowEdit`, which records the value it
 * replaced into the same history Basic uses (`BasicEngine.recordExternal`).
 *
 * Material rows write to "the selected part", read at WRITE time. An undo that
 * ran after the user clicked another part would repaint the wrong one, so the
 * ctx is pinned to the part that was selected when the edit was made.
 */

type AnyBinding = Binding<unknown>;

export function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === "number" && typeof b === "number") return Math.abs(a - b) < 1e-12 || (Number.isNaN(a) && Number.isNaN(b));
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((v, i) => same(v, b[i]));
  return false;
}

/** A ctx whose "selected part" is frozen at `sel`. While another part is
 *  selected, the material slice reads as empty so no optimistic patch lands on
 *  that other part's panel; the write still goes to `sel`. */
export function pinnedCtx(store: EditorStore, ctx: BindingCtx, sel: string | null): BindingCtx {
  return {
    ...ctx,
    selectionId: sel,
    read: (d) => {
      if (d === "selection") return sel;
      if (d === "material" && store.getState("selection") !== sel) return null;
      return store.getState(d);
    },
  };
}

/** Write one binding and record it. Returns false when nothing changed. */
export function recordedWrite(
  store: EditorStore,
  ctx: BindingCtx,
  binding: AnyBinding,
  next: unknown,
  label: string,
  key?: string,
): boolean {
  // `ui` is editor chrome (which panel is open, follow-selection) — not an
  // edit to the piece, and not something Undo should walk through.
  if (binding.domain === "ui") {
    void store.mutate(binding.domain, binding.set(next, ctx));
    return true;
  }
  const read = (d: Parameters<BindingCtx["read"]>[0]) => store.getState(d);
  const before = binding.get(read, ctx);
  if (same(before, next)) return false;
  const sel = (store.getState("selection") as string | null) ?? null;
  const pinned = pinnedCtx(store, ctx, sel);
  void store.mutate(binding.domain, binding.set(next, pinned));
  const part = binding.domain === "material" && sel ? sel : undefined;
  basicEngineFor(store).recordExternal({
    label,
    key,
    part,
    undo: () => store.mutate(binding.domain, binding.set(before, pinnedCtx(store, ctx, sel))),
    redo: () => store.mutate(binding.domain, binding.set(next, pinnedCtx(store, ctx, sel))),
  });
  return true;
}

/**
 * Several bindings as ONE undo step (a section preset, a paste). Each value is
 * written through its own binding, exactly as dragging that row would.
 */
export function recordedBatch(
  store: EditorStore,
  ctx: BindingCtx,
  writes: { binding: AnyBinding; value: unknown }[],
  label: string,
): number {
  const read = (d: Parameters<BindingCtx["read"]>[0]) => store.getState(d);
  const sel = (store.getState("selection") as string | null) ?? null;
  const changed = writes
    .map((w) => ({ ...w, before: w.binding.get(read, ctx) }))
    .filter((w) => w.binding.domain !== "ui" && !same(w.before, w.value));
  if (changed.length === 0) return 0;
  const run = (pick: "value" | "before") =>
    Promise.all(changed.map((w) => store.mutate(w.binding.domain, w.binding.set(w[pick], pinnedCtx(store, ctx, sel))))).then(() => undefined);
  void run("value");
  const part = sel && changed.some((w) => w.binding.domain === "material") ? sel : undefined;
  basicEngineFor(store).recordExternal({ label, part, undo: () => run("before"), redo: () => run("value") });
  return changed.length;
}

/** The hook rows use: `edit(binding, value, label, key)`. */
export function useRowEdit() {
  const store = useEditorStore();
  return useCallback(
    (ctx: BindingCtx, binding: AnyBinding, next: unknown, label: string, key?: string) =>
      recordedWrite(store, ctx, binding, next, label, key),
    [store],
  );
}
